import React, { useMemo } from "react";
import { AlertTriangle } from "lucide-react";

import { Button } from "@/components/ui/Button";
import Callout from "@/components/ui/Callout";
import { Checkbox } from "@/components/ui/Checkbox";
import { CenteredSpinner } from "@/components/shared/Loader";

/**
 * The module tree, for all three places a SuperAdmin sees a tenant's plan:
 * tenant create, tenant edit, and the read-only view panel.
 *
 * One component, because the child-indent rule is the easy thing to get
 * wrong, and three copies would get it wrong in three different ways.
 *
 * ## The child rule
 *
 * A child module is part of its parent's product and has no entry point of
 * its own, so it is usable only while the parent is. Unticking the parent
 * (the caller's `onToggle`, not this component) takes every child with it —
 * a locked child left ticked would still submit as granted, handing the
 * tenant a module the SuperAdmin just turned off. So while the parent is
 * off, the child renders **indented, disabled, and unticked**, with the
 * reason next to it. Re-ticking the parent unlocks the children but does not
 * restore them; the SuperAdmin picks them again.
 *
 * ## Availability is shown, never edited
 *
 * A module that is dark fleet-wide can still be granted — the tenant will own
 * it and get it the moment it is switched back on. The inline warning says
 * so, because granting something that visibly does not work looks like a bug.
 *
 * @param {Array}   modules   rows carrying `{code, name, description, parent, stored, effective, available}`
 * @param {Set}     selected  codes currently ticked. For create, the initial plan.
 * @param {Func}    onToggle  called with a code, or `"__all__"` / `"__none__"`
 * @param {boolean} readOnly  render the state with no controls at all
 * @param {Func}    [renderExtra] `(module, { checked }) => node` — extra content
 *                  nested under a module's row (e.g. the CV template picker
 *                  under CV Formatting). Rendered **outside** the row's
 *                  `<label>` on purpose: a click inside `renderExtra` must
 *                  never bubble to the label and toggle the module itself.
 *                  Generic on purpose — this component knows nothing about
 *                  what any particular module's extra content is.
 */
const ModuleSelector = ({ modules, selected, onToggle, loading, disabled, readOnly = false, renderExtra }) => {
  // Parents first, each followed by its own children. Registry order is kept
  // within each level, so the list reads the same way every time.
  const ordered = useMemo(() => {
    const rows = modules ?? [];
    const parents = rows.filter((m) => !m.parent);
    const childrenOf = (code) => rows.filter((m) => m.parent === code);
    return parents.flatMap((parent) => [parent, ...childrenOf(parent.code)]);
  }, [modules]);

  const byCode = useMemo(() => new Map((modules ?? []).map((m) => [m.code, m])), [modules]);

  if (loading) {
    return <CenteredSpinner className="h-24 rounded-lg border border-border-primary" iconClassName="h-4 w-4" />;
  }

  if (!ordered.length) {
    return <p className="rounded-lg border border-border-primary p-3 text-xs text-text-secondary">Module list unavailable.</p>;
  }

  const darkSelected = ordered.filter((m) => selected.has(m.code) && m.available === false);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className="text-xs text-text-primary/60">
          {selected.size} of {ordered.length} selected
        </span>
        {!readOnly && (
          <div className="flex gap-2">
            <Button
              disabled={disabled}
              onClick={() => onToggle("__all__")}
              className="rounded-full border border-border-primary px-2.5 py-1 text-[11px] text-text-secondary disabled:opacity-50"
            >
              Select all
            </Button>
            <Button
              disabled={disabled}
              onClick={() => onToggle("__none__")}
              className="rounded-full border border-border-primary px-2.5 py-1 text-[11px] text-text-secondary disabled:opacity-50"
            >
              Clear all
            </Button>
          </div>
        )}
      </div>

      <div className="max-h-80 overflow-y-auto custom-scrollbar rounded-lg border border-border-primary">
        {ordered.map((module) => {
          const checked = selected.has(module.code);
          const parent = module.parent ? byCode.get(module.parent) : null;
          // A child is locked while its parent is unticked. Read the parent's
          // tick, not the served `effective`, so the lock follows what the
          // SuperAdmin is doing right now rather than what was last saved.
          const lockedByParent = !!module.parent && !selected.has(module.parent);
          const controlDisabled = readOnly || disabled || lockedByParent;

          return (
            <div key={module.code} className={`border-b border-border-primary/50 p-3 last:border-b-0 ${module.parent ? "pl-9 bg-background-primary/20" : ""}`}>
              <label htmlFor={`module-${module.code}`} className={`flex items-start gap-3 ${controlDisabled ? "opacity-60" : "cursor-pointer"}`}>
                <Checkbox id={`module-${module.code}`} checked={checked} disabled={controlDisabled} onCheckedChange={() => onToggle(module.code)} className="mt-0.5" />
                <div className="flex flex-col gap-0.5">
                  <span className="text-sm text-text-primary">{module.name}</span>
                  <span className="text-[11px] leading-relaxed text-text-primary/60">{module.description}</span>

                  {lockedByParent && <span className="mt-1 text-[11px] text-text-secondary">Requires {parent?.name ?? module.parent}.</span>}

                  {checked && module.available === false && (
                    <span className="mt-1 flex items-start gap-1.5 text-[11px] text-tone-amber">
                      <AlertTriangle className="mt-0.5 h-3 w-3 flex-shrink-0" />
                      Currently switched off for every tenant — this tenant owns it but cannot use it until it is re-enabled.
                    </span>
                  )}
                </div>
              </label>

              {renderExtra ? renderExtra(module, { checked }) : null}
            </div>
          );
        })}
      </div>

      {darkSelected.length > 0 && (
        <Callout size="xs" className="px-3 py-2">
          {darkSelected.length} {darkSelected.length === 1 ? "module is" : "modules are"} switched off fleet-wide right now. They will start working for this tenant
          automatically when re-enabled.
        </Callout>
      )}
    </div>
  );
};

export default ModuleSelector;
